<script setup>
import { ref } from 'vue';
import { API } from '@/services/index';
import { useQueryClient, useMutation } from '@tanstack/vue-query';
import { useUserStore } from '@/stores/user.store.js';
import { useAccountStore } from '@/stores/account.store.js';
import { useWorkspaceStore } from '@/stores/workspace.store.js';
import { GRANT_INVITE_WORKSPACE_USER_ROLE } from '@/services/workspace/mutations/workspace/index.js';
import { useSettingsStore } from '@/stores/settings.store.js';

const formRules = {
  emails: (value) => value.length > 0 || 'Email field is required.',
  role: (value) => !!value || 'Role field is required.'
};

const props = defineProps({
  isDisabled: {
    type: Boolean,
    default: false
  },
  accountRoles: {
    type: Array,
    default: () => [],
    required: true
  }
});

const queryClient = useQueryClient();
const dialog = ref(false);
const activeAccount = computed(() => useSettingsStore().activeAccount);
const activeWorkspace = computed(() => useSettingsStore().activeWorkspace);
const isPermissionsLoading = computed(() => useUserStore().isPermissionsLoading);
const isFormValid = ref(false);
const formData = ref({
  emails: '',
  role: null,
  workspaces: [],
  message: ''
});

const { isPending, isError, error, isSuccess, mutate } = useMutation({
  mutationFn: async (payload) => {
    return await API.sendQuery('/graphql/workspace', GRANT_INVITE_WORKSPACE_USER_ROLE, payload);
  },
  onSuccess: async (data) => {
    if (data.data.data.grantUserRole) {
      dialog.value = false;
      clearForm();
      await queryClient.invalidateQueries({ queryKey: ['loadWorkspaceTeamMembers'] });
    }

    if (data.data.error) {
      console.log(data.data.error);
    }
  },
  onError: (error) => {
    console.log('error', error);
  }
});

const onSubmitHandler = async () => {
  const payload = {
    account_id: Number(activeAccount.value.id),
    email: formData.value.emails,
    role: formData.value.role,
    workspace_ids: [Number(activeWorkspace.value.id)]
  };
  await mutate(payload);
};

const clearForm = () => {
  formData.value = {
    emails: '',
    role: null,
    workspaces: [],
    message: ''
  };
};
</script>

<template>
  <div class="tw-hidden lg:tw-block">
    <v-btn
      :disabled="isPermissionsLoading || isDisabled"
      @click="dialog = true"
      class="tw-text-[0.79rem] tw-capitalize"
      color="primary-blue"
      prepend-icon="mdi-plus"
    >
      Invite Collaborators
    </v-btn>
  </div>
  <div class="tw-block lg:tw-hidden">
    <v-btn icon="mdi-account-multiple-plus" :disabled="isPermissionsLoading || isDisabled" @click="dialog = true" color="primary-blue"> </v-btn>
  </div>
  <v-dialog v-model="dialog" @update:modelValue="clearForm" width="auto" attach="#app" :persistent="isPending">
    <v-card class="tw-rounded-xl" max-width="702" width="702" :loading="isPending">
      <div class="tw-px-5 tw-pb-5">
        <h2 class="tw-my-3 tw-text-center tw-text-[0.79rem] tw-text-xl tw-font-bold tw-capitalize tw-text-primary-blue">Add Team Collaborators</h2>
        <div class="tw-w-full tw-rounded-xl tw-bg-[#f7f7f7] tw-p-3 tw-pb-10">
          <v-form class="tw-space-y-3" ref="inviteForm" v-model="isFormValid" @submit.prevent="onSubmitHandler">
            <div class="tw-flex tw-w-full tw-items-center">
              <v-textarea
                width="300"
                color="primary-blue"
                v-model="formData.emails"
                placeholder="Add emails"
                hide-details="auto"
                variant="solo"
                clearable
                rows="1"
                auto-grow
                density="compact"
                :disabled="isPending"
                :rules="[formRules.emails]"
              ></v-textarea>
            </div>
            <div class="tw-flex tw-w-full tw-items-center">
              <div class="tw-flex tw-w-full">
                <v-select
                  style="height: 40px !important"
                  v-model="formData.role"
                  :items="accountRoles"
                  placeholder="Select role"
                  item-title="name"
                  item-value="id"
                  color="primary-blue"
                  variant="solo"
                  density="compact"
                  hide-details="auto"
                  :disabled="isPending"
                  :rules="[formRules.role]"
                ></v-select>
              </div>
              <div class="tw-flex tw-w-full tw-justify-end">
                <v-btn
                  type="submit"
                  :loading="isPending"
                  :disabled="isPending || !isFormValid"
                  class="tw-capitalize"
                  width="141"
                  color="primary-blue"
                >
                  Done
                </v-btn>
              </div>
            </div>
          </v-form>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<style scoped lang="css"></style>
